import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { reapplyAlternatingRows } from "../js/alternating_rows"

export default class extends Controller {
  connect() {
    this.searchTimeout = null
    this.searchInput = document.getElementById("search-results")

    this.hollanderHandler = () => this.submit()
    this.element.addEventListener('hollander:selected', this.hollanderHandler)

    if (!this.searchInput) return

    if (this.searchInput.value !== "") {
      this.doFilter()
    }

    this.addClearButton()
  }

  disconnect() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    this.element.removeEventListener('hollander:selected', this.hollanderHandler)
  }

  filterSearch(event) {
    if (event && event.key === "Enter") {
      event.preventDefault()
      return
    }
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this.doFilter(), 150)
  }

  handleInput(event) {
    if (event.target.value === "") {
      this.showAllResults()
    }
    this.toggleClearButton()
  }

  addClearButton() {
    if (this.element.querySelector(".clear-all-search")) return

    const clearBtn = document.createElement("button")
    clearBtn.type = "button"
    clearBtn.className = "btn ms-2 clear-all-search"
    clearBtn.title = "Clear all searches"
    clearBtn.innerHTML = '<i class="fas fa-times me-1"></i>Clear'
    clearBtn.dataset.action = "click->search#clearAllSearches"

    const submitBtn = this.element.querySelector("button[data-action='search#submit']")
    if (submitBtn) submitBtn.after(clearBtn)

    this.toggleClearButton()
  }

  clearAllSearches() {
    if (!this.searchInput) return

    if (this.searchInput.value.trim() !== "") {
      this.searchInput.value = ""
      this.showAllResults()
    }

    const hollanderEl = this.element.querySelector("[data-controller='hollander-search']")
    if (hollanderEl) {
      const input = hollanderEl.querySelector("[data-hollander-search-target='input']")
      const hidden = hollanderEl.querySelector("[data-hollander-search-target='hiddenInput']")
      if (input) input.value = ""
      if (hidden) hidden.value = ""
    }

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has("query") && urlParams.get("query").trim() !== "") {
      Turbo.visit(this.element.dataset.url)
      return
    }

    this.searchInput.focus()
    this.toggleClearButton()
  }

  toggleClearButton() {
    const clearBtn = this.element.querySelector(".clear-all-search")
    if (!clearBtn) return

    const hasSearch = this.searchInput && this.searchInput.value.trim() !== ""
    const urlParams = new URLSearchParams(window.location.search)
    const hasPartFilter = urlParams.has("query") && urlParams.get("query").trim() !== ""

    clearBtn.style.display = (hasSearch || hasPartFilter) ? "" : "none"
  }

  submit() {
    const formData = new FormData(this.element)
    const queryParams = new URLSearchParams()

    for (const [key, value] of formData.entries()) {
      if (value.trim() !== "") queryParams.append(key, value)
    }

    const baseUrl = this.element.dataset.url
    const separator = queryParams.toString() ? "?" : ""
    Turbo.visit(`${baseUrl}${separator}${queryParams.toString()}`)
  }

  toggle() {
    const modalEl = document.getElementById("license-plate-modal")
    if (modalEl) {
      const modalInstance = Modal.getInstance(modalEl)
      if (modalInstance) {
        modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove(), { once: true })
        modalInstance.hide()
      } else {
        modalEl.remove()
      }
    }
    const turboModal = document.getElementById("turbo_modal")
    if (turboModal) turboModal.removeAttribute("src")
  }

  doFilter() {
    const value = this.searchInput.value.toLowerCase().trim()
    let filteredCount = 0

    this.toggleClearButton()

    if (!value) {
      this.showAllResults()
      return
    }

    const matchGroups = this.groupResultsByMatch()

    matchGroups.forEach(group => {
      const hasMatch = group.some(el =>
        el.textContent.toLowerCase().includes(value) || this.fuzzyMatch(el.textContent.toLowerCase(), value)
      )

      group.forEach(el => {
        if (hasMatch) {
          el.classList.remove("filtered-hidden")
          el.style.display = ""
          filteredCount++
        } else {
          el.classList.add("filtered-hidden")
          el.style.display = "none"
        }
      })
    })

    this.updateNoResultsDisplay(filteredCount, value)
    this.reapplyAlternatingRows()
  }

  showAllResults() {
    document.querySelectorAll("li.header-result").forEach(el => {
      el.classList.remove("filtered-hidden")
      el.style.display = ""
    })
    this.reapplyAlternatingRows()
    const noResults = document.querySelector(".no-result-wrapper")
    if (noResults) noResults.classList.add("d-none")
    this.toggleClearButton()
  }

  updateNoResultsDisplay(count, query) {
    const noResults = document.querySelector(".no-result-wrapper")
    const queryEl = document.getElementById("current-query")
    if (count === 0) {
      if (queryEl) queryEl.textContent = query
      if (noResults) noResults.classList.remove("d-none")
    } else {
      if (noResults) noResults.classList.add("d-none")
    }
  }

  groupResultsByMatch() {
    const elements = Array.from(document.querySelectorAll("li.header-result"))
    const groups = new Map()

    elements.forEach((element, idx) => {
      let matchGroup
      if (element.dataset.matchGroup && element.dataset.matchGroup.startsWith("installed_option_")) {
        matchGroup = element.dataset.matchGroup + "_" + idx
      } else {
        matchGroup = element.dataset.matchGroup ||
          element.dataset.featureId ||
          element.textContent.trim().substring(0, 50)
      }

      if (!groups.has(matchGroup)) groups.set(matchGroup, [])
      groups.get(matchGroup).push(element)
    })

    return Array.from(groups.values())
  }

  fuzzyMatch(text, query) {
    if (query.length < 3) return false
    return query.split(" ").filter(w => w.length > 2).some(w => text.includes(w))
  }

  reapplyAlternatingRows() {
    reapplyAlternatingRows()
  }
}
